import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Select, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  MONTHS, ROMAN, fmtDate, todayIso, periodEnd, computePeriodPreview,
} from './periodUtils';
import { createValue, normalizeApiError } from './analyticsIndicatorsApi';

const { Option } = Select;
const FIELD_ORDER = ['indicator', 'ptype', 'period', 'value'];

// Небольшая обёртка поля: подпись, обязательность, подсказка и сообщение об ошибке.
function Field({ label, required, hint, error, children, innerRef }) {
  const cls = 'ai-field' + (error ? ' ai-field--err' : '');
  return (
    <div className={cls} ref={innerRef}>
      <label className="ai-field__label">
        {label}{required && <span className="ai-field__req">*</span>}
      </label>
      {children}
      {hint && !error && <div className="ai-field__hint">{hint}</div>}
      {error && <div className="ai-field__msg">{error}</div>}
    </div>
  );
}

export default function IndicatorValueModal({
  open, indicators, dataLang, checkDuplicate, onClose, onSaved, onForbidden,
}) {
  const { t, i18n } = useTranslation();
  const isRu = dataLang === 'ru';
  const nm = (o, base) => (o ? o[`${base}_${dataLang}`] : null);

  const [indicatorId, setIndicatorId] = useState(null);
  const [periodType, setPeriodType] = useState(null);
  const [periodYear, setPeriodYear] = useState(null);
  const [periodIndex, setPeriodIndex] = useState(null);
  const [value, setValue] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const refs = { indicator: useRef(), ptype: useRef(), period: useRef(), value: useRef() };

  const indicator = useMemo(
    () => indicators.find((x) => x.id === indicatorId) || null,
    [indicators, indicatorId],
  );

  // Пустая форма при каждом открытии — без остаточных ошибок.
  useEffect(() => {
    if (open) {
      setIndicatorId(null); setPeriodType(null); setPeriodYear(null);
      setPeriodIndex(null); setValue(''); setErrors({}); setSaving(false);
    }
  }, [open]);

  const setErr = (patch) => setErrors((e) => ({ ...e, ...patch }));
  const clearErr = (name) => setErrors((e) => { const c = { ...e }; delete c[name]; return c; });
  const resolveErr = (err) => (err ? (err.key ? t(err.key) : err.text) : null);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    const out = [];
    for (let y = now; y >= 2001; y--) out.push(y);
    return out;
  }, []);

  // Индекс периода внутри года зависит от типа периода.
  const indexOptions = useMemo(() => {
    if (!periodType || periodType === 'year') return [];
    if (periodType === 'half') return [1, 2].map((i) => ({ v: i, text: `${ROMAN[i - 1]} ${t('analyticsIndicators.halfLabel')}` }));
    if (periodType === 'quarter') return [1, 2, 3, 4].map((i) => ({ v: i, text: `${i} ${t('analyticsIndicators.quarterLabel')}` }));
    return MONTHS[dataLang].map((m, idx) => ({ v: idx + 1, text: m }));
  }, [periodType, dataLang, i18n.language, t]);

  // Автополя: единица измерения и подпись периода (превью того, что вернёт API).
  const unitText = indicator ? nm(indicator, 'unit') : null;
  const preview = useMemo(() => {
    const ready = indicator && periodType && periodYear && (periodType === 'year' || periodIndex);
    if (!ready) return null;
    return computePeriodPreview(
      { indicator, periodType, periodYear, periodIndex }, dataLang,
    );
  }, [indicator, periodType, periodYear, periodIndex, dataLang]);

  // Смена индикатора: подставить default_period_type, если текущий недопустим.
  const onIndicatorChange = (id) => {
    const it = indicators.find((x) => x.id === id) || null;
    setIndicatorId(id);
    clearErr('indicator');
    if (it) {
      const stillOk = periodType && it.allowed_period_types.includes(periodType);
      if (!stillOk) { setPeriodType(it.default_period_type); setPeriodIndex(null); }
    }
  };

  const onPeriodTypeChange = (key) => {
    setPeriodType(key);
    setPeriodIndex(null);
    clearErr('ptype'); clearErr('period');
  };

  const onValueChange = (e) => {
    let v = e.target.value.replace(/[^0-9.,\s-]/g, '');
    // numeric(18,4): не более 14 знаков в целой части и 4 после разделителя
    const norm = v.replace(/[\s\u00A0]/g, '').replace(',', '.');
    const m = norm.match(/^-?(\d*)(?:\.(\d*))?$/);
    if (m) {
      const intLen = (m[1] || '').length;
      const decLen = (m[2] || '').length;
      if (intLen <= 14 && decLen <= 4) { setValue(v); if (v.trim() !== '') clearErr('value'); }
    } else {
      setValue(v); if (v.trim() !== '') clearErr('value');
    }
  };

  function validate() {
    const next = {};
    if (!indicatorId) next.indicator = { key: 'analyticsIndicators.err.req' };
    if (!periodType) next.ptype = { key: 'analyticsIndicators.err.req' };

    if (!periodYear || (periodType && periodType !== 'year' && !periodIndex)) {
      next.period = { key: 'analyticsIndicators.err.req' };
    } else if (periodType) {
      const idx = periodType === 'year' ? null : periodIndex;
      const pe = periodEnd(periodType, periodYear, idx);
      if (pe > todayIso()) next.period = { key: 'analyticsIndicators.err.future' };
      else if (indicatorId && checkDuplicate && checkDuplicate(indicatorId, pe)) {
        next.period = { key: 'analyticsIndicators.err.dup' };
      }
    }

    const raw = value.trim();
    if (!raw) next.value = { key: 'analyticsIndicators.err.req' };
    else {
      const n = raw.replace(/[\s\u00A0]/g, '').replace(',', '.');
      if (!/^-?\d+(\.\d+)?$/.test(n)) next.value = { key: 'analyticsIndicators.err.num' };
      else if (n.includes('.') && n.split('.')[1].length > 4) next.value = { key: 'analyticsIndicators.err.prec' };
      else if (Number(n) < 0 && !(indicator && indicator.allow_negative)) next.value = { key: 'analyticsIndicators.err.neg' };
    }

    setErrors(next);
    const firstBad = FIELD_ORDER.find((f) => next[f]);
    if (firstBad) {
      const el = refs[firstBad].current;
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const focusable = el && el.querySelector('input, .ant-select-selector');
      if (focusable && focusable.focus) setTimeout(() => focusable.focus(), 0);
      return false;
    }
    return true;
  }

  async function handleSave() {
    if (saving) return;                 // повторные нажатия блокируются
    if (!validate()) return;
    setSaving(true);
    try {
      const row = await createValue(
        { indicator_id: indicatorId, period_type: periodType, period_year: periodYear, period_index: periodIndex, value: value.trim() },
        { indicator },
      );
      onSaved(row);                     // страница: перезагрузка первой страницы + тост
    } catch (err) {
      const info = normalizeApiError(err);
      if (info.forbidden) { onForbidden(); onClose(); return; }
      if (info.fields && info.fields.length) {
        const patch = {};
        info.fields.forEach((f) => {
          patch[f.field] = f.messageKey ? { key: f.messageKey } : { text: f.message || t('analyticsIndicators.err.save') };
        });
        setErrors((e) => ({ ...e, ...patch }));
        const firstBad = FIELD_ORDER.find((f) => patch[f]);
        if (firstBad && refs[firstBad].current) refs[firstBad].current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } else {
        Modal.error({ title: t('analyticsIndicators.err.saveTitle'), content: t('analyticsIndicators.err.save') });
      }
    } finally {
      setSaving(false);                 // данные формы не теряются
    }
  }

  const periodTypeOptions = useMemo(() => (
    // Полугодие присутствует, но у всех индикаторов disabled (вводится как II квартал).
    ['year', 'half', 'quarter', 'month'].map((key) => ({
      key,
      text: t(`analyticsIndicators.pt.${key}`),
      disabled: indicator ? !indicator.allowed_period_types.includes(key) : false,
    }))
  ), [indicator, i18n.language, t]);

  return (
    <Modal
      className="ai-modal"
      open={open}
      width={640}
      title={t('analyticsIndicators.form.title')}
      onCancel={onClose}                // Esc и клик вне окна — как «Отмена» (АС1)
      maskClosable
      keyboard
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>{t('analyticsIndicators.cancel')}</Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          {t('analyticsIndicators.save')}
        </Button>,
      ]}
    >
      {/* 1. Индикатор */}
      <Field
        innerRef={refs.indicator}
        label={t('analyticsIndicators.form.indicator')}
        required
        hint={t('analyticsIndicators.hint.indicator')}
        error={resolveErr(errors.indicator)}
      >
        <Select
          showSearch
          value={indicatorId || undefined}
          placeholder={t('analyticsIndicators.selectPlaceholder')}
          optionFilterProp="label"
          style={{ width: '100%' }}
          onChange={onIndicatorChange}
          filterOption={(input, opt) => (opt.label || '').toLowerCase().includes(input.toLowerCase())}
        >
          {indicators.map((it) => {
            const label = nm(it, 'name') + (nm(it, 'sector') ? ` · ${nm(it, 'sector')}` : '');
            return <Option key={it.id} value={it.id} label={label}>{label}</Option>;
          })}
        </Select>
      </Field>

      <div className="ai-row2">
        {/* 2. Тип периода */}
        <Field
          innerRef={refs.ptype}
          label={t('analyticsIndicators.form.periodType')}
          required
          hint={t('analyticsIndicators.hint.half')}
          error={resolveErr(errors.ptype)}
        >
          <Select
            value={periodType || undefined}
            placeholder={t('analyticsIndicators.selectPlaceholder')}
            style={{ width: '100%' }}
            onChange={onPeriodTypeChange}
          >
            {periodTypeOptions.map((o) => (
              <Option key={o.key} value={o.key} disabled={o.disabled}>{o.text}</Option>
            ))}
          </Select>
        </Field>

        {/* 3. Единица измерения — заполняется системой */}
        <Field label={t('analyticsIndicators.form.unit')} hint={t('analyticsIndicators.hint.unit')}>
          <div className={'ai-auto' + (unitText ? '' : ' ai-auto--empty')}>{unitText || '—'}</div>
        </Field>
      </div>

      {/* 4. Период */}
      <Field
        innerRef={refs.period}
        label={t('analyticsIndicators.form.period')}
        required
        hint={preview
          ? `${t('analyticsIndicators.hint.saved')}: ${fmtDate(preview.period_start)} – ${fmtDate(preview.period_end)}`
          : t('analyticsIndicators.hint.period')}
        error={resolveErr(errors.period)}
      >
        <div className="ai-row2">
          <Select
            value={periodYear || undefined}
            placeholder={t('analyticsIndicators.selectYear')}
            style={{ width: '100%' }}
            onChange={(y) => { setPeriodYear(y); clearErr('period'); }}
          >
            {years.map((y) => <Option key={y} value={y}>{y}</Option>)}
          </Select>
          {periodType !== 'year' && (
            <Select
              value={periodIndex || undefined}
              placeholder={t('analyticsIndicators.selectPlaceholder')}
              style={{ width: '100%' }}
              disabled={!periodType}
              onChange={(i) => { setPeriodIndex(i); clearErr('period'); }}
            >
              {indexOptions.map((o) => <Option key={o.v} value={o.v}>{o.text}</Option>)}
            </Select>
          )}
        </div>
      </Field>

      {/* 5. Подпись периода на дэшборде — формируется системой */}
      <Field label={t('analyticsIndicators.form.comment')} hint={t('analyticsIndicators.hint.comment')}>
        <div className={'ai-auto' + (preview ? '' : ' ai-auto--empty')}>
          {preview ? preview.label : '—'}
        </div>
      </Field>

      {/* 6. Значение индикатора */}
      <Field
        innerRef={refs.value}
        label={t('analyticsIndicators.form.value')}
        required
        hint={t('analyticsIndicators.hint.value')}
        error={resolveErr(errors.value)}
      >
        <Input
          value={value}
          inputMode="decimal"
          placeholder={t('analyticsIndicators.phValue')}
          onChange={onValueChange}
          onPressEnter={handleSave}
        />
      </Field>
    </Modal>
  );
}
